/**
 * 🔒 P2 보안 개선: 로그인 Rate Limiting 서비스
 *
 * 연속 로그인 실패 시 임시 차단 기능
 * - 5회 연속 실패 → 5분 차단
 * - localStorage로 클라이언트 측 차단 (빠른 응답)
 * - Firestore 로깅으로 서버 측 감사 추적
 */

import { securityLogService } from './securityLogService.ts';
import { logger } from '../utils/logger.ts';

// Rate Limiting 설정
const RATE_LIMIT_CONFIG = {
  maxAttempts: 5,           // 최대 시도 횟수
  lockoutDuration: 5 * 60 * 1000,  // 차단 시간 (5분, 밀리초)
  storagePrefix: 'login_attempts_'
};

// 로그인 시도 정보 인터페이스
interface LoginAttemptInfo {
  email: string;
  attempts: number;
  firstAttemptAt: number;
  lockedUntil: number | null;
  lastAttemptAt: number;
}

class RateLimitService {
  /**
   * localStorage 키 생성
   */
  private getStorageKey(email: string): string {
    // 이메일을 해시하여 키로 사용 (개인정보 보호)
    const hash = this.simpleHash(email.toLowerCase());
    return `${RATE_LIMIT_CONFIG.storagePrefix}${hash}`;
  }

  /**
   * 간단한 해시 함수 (개인정보 보호용)
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 32bit 정수로 변환
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * 로그인 시도 정보 조회
   */
  private getAttemptInfo(email: string): LoginAttemptInfo | null {
    try {
      const key = this.getStorageKey(email);
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const info = JSON.parse(stored) as LoginAttemptInfo;

      // 차단 시간이 지났으면 정보 삭제
      if (info.lockedUntil && Date.now() > info.lockedUntil) {
        this.clearAttempts(email);
        return null;
      }

      return info;
    } catch (error) {
      logger.error('[RateLimitService] Error getting attempt info:', error instanceof Error ? error : undefined);
      return null;
    }
  }

  /**
   * 로그인 시도 정보 저장
   */
  private saveAttemptInfo(info: LoginAttemptInfo): void {
    try {
      const key = this.getStorageKey(info.email);
      localStorage.setItem(key, JSON.stringify(info));
    } catch (error) {
      logger.error('[RateLimitService] Error saving attempt info:', error instanceof Error ? error : undefined);
    }
  }

  /**
   * 로그인 시도 정보 삭제
   */
  clearAttempts(email: string): void {
    try {
      const key = this.getStorageKey(email);
      localStorage.removeItem(key);
      logger.debug('[RateLimitService] Cleared login attempts for user');
    } catch (error) {
      logger.error('[RateLimitService] Error clearing attempts:', error instanceof Error ? error : undefined);
    }
  }

  /**
   * 로그인 차단 여부 확인
   *
   * @returns { blocked: boolean, remainingTime?: number, attempts?: number }
   */
  checkRateLimit(email: string): {
    blocked: boolean;
    remainingTime?: number;
    remainingAttempts?: number;
    message?: string;
  } {
    const info = this.getAttemptInfo(email);

    if (!info) {
      return {
        blocked: false,
        remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts
      };
    }

    // 차단 중인지 확인
    if (info.lockedUntil && Date.now() < info.lockedUntil) {
      const remainingTime = Math.ceil((info.lockedUntil - Date.now()) / 1000);
      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;

      return {
        blocked: true,
        remainingTime,
        message: `로그인이 일시적으로 차단되었습니다. ${minutes}분 ${seconds}초 후에 다시 시도해주세요.`
      };
    }

    return {
      blocked: false,
      remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - info.attempts
    };
  }

  /**
   * 로그인 실패 기록
   *
   * @returns 차단 여부와 남은 시도 횟수
   */
  async recordFailedAttempt(email: string, reason: string): Promise<{
    blocked: boolean;
    remainingAttempts: number;
    message?: string;
  }> {
    const now = Date.now();
    let info = this.getAttemptInfo(email);

    if (!info) {
      info = {
        email: email.toLowerCase(),
        attempts: 0,
        firstAttemptAt: now,
        lockedUntil: null,
        lastAttemptAt: now
      };
    }

    info.attempts += 1;
    info.lastAttemptAt = now;

    // 보안 로그 기록
    await securityLogService.logLoginFailed(email, reason, {
      attemptNumber: info.attempts,
      maxAttempts: RATE_LIMIT_CONFIG.maxAttempts
    });

    // 최대 시도 횟수 초과 시 차단
    if (info.attempts >= RATE_LIMIT_CONFIG.maxAttempts) {
      info.lockedUntil = now + RATE_LIMIT_CONFIG.lockoutDuration;

      // Rate Limit 초과 보안 로그
      await securityLogService.logSecurityEvent(
        'RATE_LIMIT_EXCEEDED',
        'anonymous',
        'LOGIN',
        [],
        {
          userEmail: email,
          attempts: info.attempts,
          lockoutDuration: RATE_LIMIT_CONFIG.lockoutDuration / 1000,
          lockoutUntil: new Date(info.lockedUntil).toISOString()
        }
      );

      logger.warn('[RateLimitService] User locked out due to too many failed attempts', {
        context: { email: email.substring(0, 3) + '***', attempts: info.attempts }
      });
    }

    this.saveAttemptInfo(info);

    if (info.lockedUntil && Date.now() < info.lockedUntil) {
      const remainingTime = Math.ceil((info.lockedUntil - Date.now()) / 1000);
      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;

      return {
        blocked: true,
        remainingAttempts: 0,
        message: `로그인 시도 횟수를 초과했습니다. ${minutes}분 ${seconds}초 후에 다시 시도해주세요.`
      };
    }

    const remainingAttempts = RATE_LIMIT_CONFIG.maxAttempts - info.attempts;

    return {
      blocked: false,
      remainingAttempts,
      message: remainingAttempts <= 2
        ? `로그인 시도 ${remainingAttempts}회 남았습니다.`
        : undefined
    };
  }

  /**
   * 로그인 성공 시 시도 정보 초기화
   */
  recordSuccessfulLogin(email: string): void {
    this.clearAttempts(email);
    logger.debug('[RateLimitService] Login successful, cleared attempt history');
  }

  /**
   * 남은 차단 시간 조회 (초 단위)
   */
  getRemainingLockoutTime(email: string): number | null {
    const info = this.getAttemptInfo(email);
    if (!info || !info.lockedUntil) return null;

    const remaining = info.lockedUntil - Date.now();
    return remaining > 0 ? Math.ceil(remaining / 1000) : null;
  }
}

// 싱글톤 인스턴스 export
export const rateLimitService = new RateLimitService();

export default rateLimitService;
